import React, { useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useColors } from "../constants/Colors";

const LANGUAGES = [
  { code: "en", name: "English" },
  { code: "de", name: "Deutsch" },
  { code: "fr", name: "Français" },
  { code: "it", name: "Italiano" },
  { code: "es", name: "Español" },
  { code: "pt", name: "Português" },
  { code: "nl", name: "Nederlands" },
  { code: "pl", name: "Polski" },
  { code: "ru", name: "Русский" },
  { code: "zh", name: "中文" },
  { code: "ja", name: "日本語" },
  { code: "ko", name: "한국어" },
  { code: "ar", name: "العربية" },
  { code: "tr", name: "Türkçe" },
  { code: "uk", name: "Українська" },
];

interface TranslateButtonProps {
  isTranslating: boolean;
  showTranslation: boolean;
  showLanguageSelector: boolean;
  selectedLanguage: string;
  translatedText: string | null;
  onToggleTranslation: () => void;
  onToggleLanguageSelector: () => void;
  onSelectLanguage: (langCode: string) => void;
  onCloseLanguageSelector: () => void;
}

const TranslateButton: React.FC<TranslateButtonProps> = ({
  isTranslating,
  showTranslation,
  showLanguageSelector,
  selectedLanguage,
  translatedText,
  onToggleTranslation,
  onToggleLanguageSelector,
  onSelectLanguage,
  onCloseLanguageSelector,
}) => {
  const { t } = useTranslation();
  const { accentColor, textNeutralOnPrimary, textNeutralDefault } = useColors();
  const selectorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        selectorRef.current &&
        !selectorRef.current.contains(event.target as Node)
      ) {
        onCloseLanguageSelector();
      }
    };

    if (showLanguageSelector) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showLanguageSelector, onCloseLanguageSelector]);

  return (
    <div className="relative" ref={selectorRef}>
      <button
        type="button"
        onClick={() => {
          if (showTranslation) {
            onToggleTranslation();
          } else {
            onToggleLanguageSelector();
          }
        }}
        disabled={isTranslating}
        className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-medium transition-all duration-150 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50"
        style={{
          backgroundColor: showTranslation
            ? accentColor
            : "rgba(2, 2, 16, 0.05)",
          color: showTranslation ? textNeutralOnPrimary : textNeutralDefault,
        }}
      >
        {isTranslating ? (
          <>
            <svg
              className="animate-spin h-3.5 w-3.5"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <span>{t("translating") || "Translating..."}</span>
          </>
        ) : (
          <>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="m5 8 6 6" />
              <path d="m4 14 6-6 2-3" />
              <path d="M2 5h12" />
              <path d="M7 2h1" />
              <path d="m22 22-5-10-5 10" />
              <path d="M14 18h6" />
            </svg>
            <span>
              {showTranslation
                ? t("show_original") || "Original"
                : t("translate") || "Translate"}
            </span>
            {!showTranslation && (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="10"
                height="10"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{
                  transform: showLanguageSelector
                    ? "rotate(180deg)"
                    : "rotate(0deg)",
                  transition: "transform 0.2s ease",
                }}
              >
                <path d="m6 9 6 6 6-6" />
              </svg>
            )}
          </>
        )}
      </button>

      {showLanguageSelector && (
        <div
          className="absolute right-0 top-full mt-2 p-1.5 rounded-2xl z-50 max-h-64 overflow-y-auto min-w-[180px]"
          style={{
            backgroundColor: "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(16px)",
            WebkitBackdropFilter: "blur(16px)",
            boxShadow:
              "0 8px 40px rgba(2, 2, 16, 0.12), 0 0 0 1px rgba(2, 2, 16, 0.06)",
            borderRadius: 20,
            animation:
              "scaleIn 0.18s cubic-bezier(0.16, 1, 0.3, 1) forwards",
            transformOrigin: "top right",
          }}
        >
          {LANGUAGES.map((lang) => {
            const isActive =
              selectedLanguage === lang.code && !!translatedText;
            return (
              <button
                key={lang.code}
                type="button"
                onClick={() => onSelectLanguage(lang.code)}
                className="w-full px-3.5 py-2.5 text-left text-sm transition-all duration-100 flex items-center justify-between rounded-xl"
                style={{
                  color: isActive ? accentColor : textNeutralDefault,
                  backgroundColor: "transparent",
                  fontWeight: isActive ? 600 : 400,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor =
                    "rgba(2, 2, 16, 0.04)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                }}
              >
                <span>{lang.name}</span>
                {isActive && (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={accentColor}
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TranslateButton;
